import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AnalyticsClient, PAGE_ANALYTICS, PageAnalyticsMetadata } from '../../shared/constants/page-analytics.constants';

interface TagManagerEvent {
  event: string;
  [key: string]: string | undefined;
}

declare global {
  interface Window {
    dataLayer: TagManagerEvent[];
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleTagManagerService {
  private initialized = false;

  public initialize(): void {
    const containerId = environment.analytics.gtmContainerId;
    if (this.initialized || !environment.analytics.enabled || !containerId) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'gtm.js',
      'gtm.start': Date.now().toString(),
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    document.head.appendChild(script);

    this.initialized = true;
  }

  public trackPageView(url: string): void {
    if (!environment.analytics.enabled) {
      return;
    }

    const applicationType = this.getApplicationType(url);
    const pageMetadata = this.getPageMetadata(url, applicationType);

    const pageViewEvent: TagManagerEvent = {
      event: 'virtualPageview',
      pageTitle: pageMetadata?.pageName ?? document.title,
      pageLocation: window.location.href,
      applicationType: applicationType,
    };

    if (pageMetadata) {
      pageViewEvent['pageName'] = pageMetadata.pageName;
      pageViewEvent['pageCode'] = pageMetadata.pageCode;
    }

    this.push(pageViewEvent);
  }

  public trackEvent(event: string, parameters: Record<string, string> = {}): void {
    if (!environment.analytics.enabled || !event) {
      return;
    }

    this.push({ event, ...parameters });
  }

  private push(event: TagManagerEvent): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }

  
  private getApplicationType(path: string): string {
    const [, , applicationType] = path.split('/');
    return applicationType === 'ieee' || applicationType === 'asme'
      ? applicationType
      : 'landing';
  }

  private getPageMetadata(
    path: string,
    applicationType: string
  ): PageAnalyticsMetadata | undefined {
    if (applicationType !== 'ieee' && applicationType !== 'asme') {
      return undefined;
    }
    const pageName = path.split('/')[3];
    return PAGE_ANALYTICS[applicationType as AnalyticsClient][pageName];
  }
}